/**
 * ai-presentation.ts
 * Genereert de verhalende klant-tekst (3 tot 6 alinea's) die in de PDF-presentatie komt.
 * Geen line items, geen prijzen voor de klant — alleen sfeer + samenstelling.
 *
 * Lichte port van patterns uit tenants/2bmade/app/src/lib/gift-robin-prompt.ts,
 * maar dan voor klant-output in plaats van advies aan Mark.
 */

import { aiComplete } from './ai-complete';
import type { Client, GiftPackage, GiftPackageItem, Project } from './types';

interface GeneratePresentationInput {
  project: Project;
  pkg: GiftPackage;
  client: Client | null;
  items: GiftPackageItem[];
  /** Optionele extra-context voor Mark om de stem te sturen ("informeel", "Fries karakter", etc.) */
  extraContext?: string;
}

const SYSTEM_PROMPT = `Je schrijft een klant-presentatie voor een relatiegeschenk-pakket van 2BMade in Leeuwarden.

Mark Tromp van 2BMade stelt pakketten samen voor zakelijke klanten. Deze tekst komt in een PDF die naar de klant gaat. De klant ziet GEEN inkoopprijzen, GEEN tabellen en GEEN technische details.

Vereisten:
- 3 tot 6 alinea's, Nederlandstalig, warm en zakelijk.
- Open natuurlijk en context-passend: refereer aan het eerdere gesprek/contact met de klant, maar kies zelf een formulering die bij de relatie past. Niet alle openingen moeten hetzelfde zijn.
- Beschrijf de inhoud VERHALEND, niet als opsomming of tabel. Bijvoorbeeld: "Een stevige doos van DS Smith Box of Joy met daarin chocolade-koek van Bickery, een fles Friese Boekweit Blond van Zaailander, en een handgemaakte gadget van The Gift."
- Noem product-namen, merken en leveranciers waar dat de tekst rijker maakt (bv. "Bickery's Italiaanse cantuccini"), maar maak er geen droge lijst van.
- Eindig met een uitnodigende afsluiting die natuurlijk past bij de toon van de rest, geen vaste boilerplate-zin.
- Schrijf in normaal Nederlands. Geen woorden als 'leverage', 'robust', 'seamless', 'crucial', 'vibrant'. Geen em-dashes (—). Vervang door komma's of punten.
- Geen markdown, geen kopjes, geen bullets. Alleen losse alinea's gescheiden door een lege regel.
- Maximaal 350 woorden.

Mark gaat de tekst altijd nog zelf bijschaven, dus geef hem een goede beginversie die hij vrij kan kneden.`;

function describeItems(items: GiftPackageItem[]): string {
  const lines: string[] = [];
  for (const it of items) {
    if (it.excluded) continue;
    const p = it.gift_product;
    if (!p) continue;
    const parts: string[] = [];
    parts.push(`${it.quantity_per_package}x ${p.name}`);
    if (p.brand && p.brand !== p.name) parts.push(`merk ${p.brand}`);
    if (p.supplier?.name) parts.push(`leverancier ${p.supplier.name}`);
    if (p.category) parts.push(`categorie ${p.category}`);
    if (p.is_packaging) parts.push('(verpakking)');
    lines.push('- ' + parts.join(', '));
  }
  return lines.join('\n');
}

function buildUserMessage(input: GeneratePresentationInput): string {
  const { project, pkg, client, items, extraContext } = input;
  const clientLine = client?.company_name
    ? `Klant: ${client.company_name}${client.contact_name ? ` (contact: ${client.contact_name})` : ''}`
    : 'Klant: nog niet ingevuld';
  const themeLine = pkg.theme ? `Thema: ${pkg.theme}` : '';
  const seasonLine = pkg.season ? `Seizoen: ${pkg.season}` : '';
  const qtyLine = `Aantal pakketten: ${pkg.quantity}`;
  const desc = project.description ? `Achtergrond gesprek:\n${project.description}` : '';
  const summary = project.ai_summary ? `Eerdere AI-samenvatting:\n${project.ai_summary}` : '';
  const extra = extraContext ? `Extra wensen van Mark voor deze tekst:\n${extraContext}` : '';
  const itemsBlock = `Inhoud pakket (gebruik dit verhalend, niet als lijst in output):\n${describeItems(items)}`;

  return [clientLine, themeLine, seasonLine, qtyLine, '', desc, summary, extra, '', itemsBlock]
    .filter(Boolean)
    .join('\n');
}

export async function generatePresentationText(input: GeneratePresentationInput): Promise<string> {
  // Gemini 2.5 Flash (server-side gepind in /ai/complete): snel + sterk in NL tekst,
  // volgens tenant-context voor "Offerte tekst".
  const content = await aiComplete({
    messages: [
      { role: 'system', content: SYSTEM_PROMPT },
      { role: 'user', content: buildUserMessage(input) },
    ],
    temperature: 0.6,
    max_tokens: 900,
  });

  return cleanText(content);
}

/**
 * Verwijdert resterende AI-tics (em-dashes, markdown, dubbele lege regels).
 */
export function cleanText(raw: string): string {
  return raw
    .replace(/—/g, ',') // em-dash → komma
    .replace(/–/g, '-') // en-dash → hyphen
    .replace(/^#{1,6}\s+/gm, '') // strip markdown headers
    .replace(/^\s*[-*•]\s+/gm, '') // strip bullet markers (-, *, •)
    .replace(/\*\*([^*]+)\*\*/g, '$1') // **vet** → tekst
    .replace(/__([^_]+)__/g, '$1') // __vet__ → tekst
    .replace(/\*([^*\n]+)\*/g, '$1') // *cursief* → tekst
    .replace(/\n{3,}/g, '\n\n') // max 1 lege regel tussen alinea's
    .trim();
}
